import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import type { MultiChipItem } from '~/components/inputs';
import type { NetworkAuditLogs__User as User } from '../queries/list.generated';

const USER_SCHEMA = z.array(z.number());

export const useFilterParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  return useMemo(() => {
    const usersParams = USER_SCHEMA.parse(JSON.parse(searchParams.get('users') || '[]'));

    return {
      setSearchParams,
      usersParams,
    };
  }, [searchParams, setSearchParams]);
};

export const useFilters = (users: readonly User[]) => {
  const { setSearchParams, usersParams } = useFilterParams();

  const [auditUsers, setAuditUsers] = useState<MultiChipItem<number>[]>([]);

  useEffect(() => {
    if (users.map((x) => x.id).join() === auditUsers.map((x) => x.value).join()) return;
    setAuditUsers([
      ...users.map((x) => ({
        label: x.name,
        selected: usersParams.includes(x.id),
        value: x.id,
      })),
    ]);
  }, [users, auditUsers, usersParams]);

  const handleClear = useCallback(() => {
    setAuditUsers((current) => current.map((x) => ({ ...x, selected: false })));
    setSearchParams((params) => {
      params.delete('users');
      params.set('page', '1');
      return params;
    });
  }, [setSearchParams]);

  const updateSearchParams = useCallback(
    (name: string, state: MultiChipItem<number | string>[]) => {
      setSearchParams((params) => {
        const selected = state.filter((x) => x.selected);
        selected.length > 0
          ? params.set(name, JSON.stringify(selected.map((x) => x.value)))
          : params.delete(name);
        params.set('page', '1'); // reset pagination when filters change
        return params;
      });
    },
    [setSearchParams],
  );

  return {
    handleClear,
    setAuditUsers,
    users: auditUsers,
    updateSearchParams,
  };
};
